import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { RbacService } from '@stg-js-ngx-core/oauth';
import { Observable, throwError } from 'rxjs';
import moment from 'moment';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { PatientDataListsAPIResponse } from '../common/models/patient-data-lists.model';
import { ICDAPIResponseModel } from '../common/models/code.model';
import { PatientTransactionAPIResponse } from '../common/models/patient-transaction.model';
import { MmtCommonService } from '../common/services/mmt-common.service';
import { PatientTransactionLTCAPIResponse } from '../common/models/patient-transaction-ltc.model';
import { PatientTransactionRehabAPIResponse } from '../common/models/patient-transaction-rehab.model';
import { PatientTransactionLTACHAPIResponse } from '../common/models/patient-transaction-ltach.model';

@Injectable({
  providedIn: 'root'
})
export class PatientDataService {

  constructor(private http: HttpClient,
    private rbacService: RbacService,
    private commonService: MmtCommonService) { }

  getPatientTransactions(hospitalId, isMonthly, pageSize, pageNumber, sort, searchInput, startDate, endDate, teamName): Observable<PatientTransactionAPIResponse> {
    const body = {
      hospitalId: hospitalId,
      isMonthly: isMonthly,
      startDate: startDate,
      endDate: endDate,
      currentPageNumber: pageNumber,
      numberOfItemsOnPage: pageSize,
      ascending: false,
      search: "",
      orderBy: 'encounterid',
      modifiedBy: this.commonService.getEmail()
    };
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    return this.http.post<PatientTransactionAPIResponse>(environment.urls.getPatientTransactionsByFYV2, body, { params: httpParams })
      .pipe(
        map((response: any) => {
          if (response.data && response.data.length) {
            for (let i = 0; i < response.data.length; i++) {
              response.data[i].los = this.calculateLOS(response.data[i].admitDate, response.data[i].dischargedDate);

              if (response.data[i].rdInterventions) {
                response.data[i].rdInterventions = response.data[i].rdInterventions.join(', ');
              }
              if (response.data[i].patientUnit) {
                response.data[i].patientUnit = response.data[i].patientUnit.join(', ');
              }
            }
          }
          return response;
        }),
        catchError(error => throwError(error))
      );
  }

  getPatientTransaction(patientTransactionId, hospitalId, teamName: string): Observable<PatientTransactionAPIResponse> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    const body = {
      hospitalId: hospitalId,
      patientTransactionId: patientTransactionId
    };

    return this.http.post<PatientTransactionAPIResponse>(environment.urls.patientTransactionV2, body, { params: httpParams });
  }

  savePatientTransaction(body, teamName: string): Observable<any> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    if (body.patientTransactionId) {
      return this.http.put(environment.urls.updatePatientTransaction, body, { params: httpParams });
    } else {
      return this.http.post(environment.urls.postPatientTransaction, body, { params: httpParams });
    }
  }

  deletePatientTransaction(transactionId, hospitalId, teamName: string): Observable<any> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    const body = {
      hospitalId: hospitalId,
      patientTransactionId: transactionId,
      modifiedBy: this.rbacService.username
    };
    return this.http.post(environment.urls.deletePatientTransactionV2, body, { params: httpParams });
  }

  getListValues(teamName: string, listTypes?: string[]): Observable<PatientDataListsAPIResponse> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    return this.http.post<PatientDataListsAPIResponse>(environment.urls.getListValues, listTypes || [], { params: httpParams });
  }

  getICDCodes(hospitalId: string, teamName: string): Observable<ICDAPIResponseModel> {
    const httpParams = new HttpParams().set('hospitalId', hospitalId).set('hospitalTeamName', teamName);
    return this.http.get<ICDAPIResponseModel>(environment.urls.getICDCodes, { params: httpParams });
  }

  getMSDRGs(hospitalId: string, date: string, teamName: string, careTypeId?: string | number): Observable<any> {
    let httpParams = new HttpParams().set('hospitalId', hospitalId).set('dischargeDate', date).set('hospitalTeamName', teamName);
    if (careTypeId !== null && careTypeId !== undefined) {
      httpParams = httpParams.set('CareTypeId', careTypeId.toString());
    }
    return this.http.get<ICDAPIResponseModel>(environment.urls.getMSDRGs, { params: httpParams });
  }

  calculateLOS(admitDate, dischargeDate) {
    if (!admitDate || !dischargeDate) {
      return null;
    } else {
      const startDate = moment(new Date(admitDate));
      const endDate = moment(new Date(dischargeDate));
      if (endDate.isSame(startDate)) { return (endDate.diff(startDate, 'days')) + 1; }
      return endDate.diff(startDate, 'days');
    }
  }

  triggerDenialEmailAlert(costCenter, facilityName, encounterID, denialDate, denialReason) {
    const body = {
      CostCenter: costCenter,
      FacilityName: facilityName,
      EncounterID: encounterID,
      DenialDate: denialDate,
      DenialReason: denialReason.map(x => x).join(";"),
      ToEmail: environment.constants.denialEmail,
      CcEmails: ''
    };
    this.http.post(environment.urls.emailNewDenialAlert, body).subscribe();
  }

  getDRGWithoutMaln(dischargeDate, icdCode, drgValue, onlyMCC, onlyCCAndNoMCC, onlyCC, teamName, careTypeId: number): Observable<any> {
    const httpParams = new HttpParams().set('dischargedDate', dischargeDate).set('icdCode', icdCode).set('dRGWMalnutrition', drgValue).set('onlyMCC', onlyMCC === null ? '' : onlyMCC).set('onlyCCAndNoMCC', onlyCCAndNoMCC === null ? '' : onlyCCAndNoMCC).set('onlyCC', onlyCC === null ? '' : onlyCC).set('hospitalTeamName', teamName).set('CareTypeId', careTypeId.toString());
    return this.http.get<any>(environment.urls.getDRGWoMalnutrition, { params: httpParams });
  }

  getDuplicateRecords(mrnValue, admitDate, hospitalId, pinValue): Observable<any> {
    const httpParams = new HttpParams().set('mrn', mrnValue).set('admitDate', admitDate)
      .set('hospitalId', hospitalId).set('pin', pinValue);
    return this.http.get(environment.urls.getDuplicateRecords, { params: httpParams });
  }

  getPatientTransactionsLTC(facilityId, isMonthly, ltcPatientPageId, pageSize, pageNumber, sort, searchInput, startDate, endDate, teamName): Observable<PatientTransactionLTCAPIResponse> {
    const body = {
      facilityId: facilityId,
      isMonthly: isMonthly,
      ltcPatientPageId: ltcPatientPageId,
      startDate: startDate,
      endDate: endDate,
      currentPageNumber: pageNumber,
      numberOfItemsOnPage: pageSize,
      ascending: false,
      search: "",
      orderBy: 'encounterid',
      modifiedBy: this.commonService.getEmail()
    };
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    return this.http.post<PatientTransactionLTCAPIResponse>(environment.urls.getPatientTransactionsLTC, body, { params: httpParams })
      .pipe(
        map((response: any) => {
          if (response.data?.length) {
            for (let i = 0; i < response.data.length; i++) {
              response.data[i].losMedicareAOrPDPM = this.calculateLOS(response.data[i].admitDate, response.data[i].dischargedDateMedicareAOrPDPM);

              if (response.data[i].rdInterventions) {
                response.data[i].rdInterventions = response.data[i].rdInterventions.join(', ');
              }
            }
          }
          return response;
        }),
        catchError(error => throwError(error))
      );
  }

  getPatientTransactionByIdLTC(residentTransactionId, hospitalId, teamName: string): Observable<PatientTransactionLTCAPIResponse> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    const body = {
      facilityId: hospitalId,
      residentTransactionId: residentTransactionId
    };

    return this.http.post<PatientTransactionLTCAPIResponse>(environment.urls.patientTransactionLTC, body, { params: httpParams });
  }

  savePatientTransactionLTC(body, teamName: string): Observable<any> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    if (body.residentTransactionId) {
      return this.http.put(environment.urls.updatePatientTransactionLTC, body, { params: httpParams });
    } else {
      return this.http.post(environment.urls.InsertOrUpdatePatientTransaction, body, { params: httpParams });
    }
  }

  deletePatientTransactionLTC(transactionId, facilityId, teamName: string): Observable<any> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    const body = {
      facilityId: facilityId,
      residentTransactionId: transactionId
    };
    return this.http.post(environment.urls.patientTransactionDeleteLTC, body, { params: httpParams });
  }

  getLTCPatientTransactionNutritionData(LTCResidentTransactionId, facilityId, teamName): Observable<any> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    const body =
    {
      facilityId: facilityId,
      LTCResidentTransactionId: LTCResidentTransactionId
    }
    return this.http.post<any>(environment.urls.getPatientNutritionStatusDataLTC, body, { params: httpParams });
  }

  saveLTCPatientTransactionNutritionData(body: { ltcResidentTransactionId: any; encounterId: any; statusChangeDate: any; rdnFirstName: any; rdnLastName: any; rdnMalnutritionDx: any; bmi: any; patientOrResidentUnit: any; rdInterventions: any; dxOfDehydration: boolean; classifiedFallRisk: boolean; residentFallDuringPrevious12Months: boolean; pressureInjuryIdentified: boolean; highestPressureInjury: any; residentExpiredOrDischargedDate: any; reasonforDischarge: any; }, teamName): Observable<any> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    return this.http.post<any>(environment.urls.savePatientNutritionStatusDataLTC, body, { params: httpParams });
  }

  getDuplicateRecordLTC(mrnValue, facilityId, teamName, admitDate, pinValue, encounterId): Observable<any> {
    const httpParams = new HttpParams().set('mrn', mrnValue).set('facilityId', facilityId).set('hospitalTeamName', teamName).set('admitDate', admitDate).set('pin', pinValue).set('encounterId', encounterId);
    return this.http.get(environment.urls.patientTransactionDuplicateLTC, { params: httpParams });
  }

  updateNutDischargeOnPatientTransaction(residentTransactionId, facilityId, dischargeDate, dischargeReason, teamName) {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    const body =
    {
      "residentTransactionId": residentTransactionId,
      "facilityId": facilityId,
      "reasonforDischarge": dischargeReason,
      "residentExpiredOrDischargedDate": dischargeDate,
    }
    return this.http.put(environment.urls.updatePatientTransactionFromNutritionOnDischarge, body, { params: httpParams });
  }

  deleteNutDischargeOnPatientTransaction(ltcNutritionStatusChangeId, teamName) {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    const body =
    {
      "LTCNutritionStatusChangeId": ltcNutritionStatusChangeId
    }
    return this.http.post<any>(environment.urls.deletePatientNutritionStatusDataLTC, body, { params: httpParams });
  }
  getUserConfigurationData(pageId): Observable<any> {
    const httpParams = new HttpParams().set('modifiedBy', this.commonService.getEmail()).set('pageId', pageId);//.set('hospitalTeamName', teamName);;
    return this.http.get(environment.urls.getUserConfiguration, { params: httpParams });
  }

  upsertUserConfigurationData(body): Observable<any> {
    const httpParams = new HttpParams();//.set('hospitalTeamName', teamName);
    return this.http.post<any>(environment.urls.upsertUserConfiguration, body, { params: httpParams });
  }

  getValidAlphaNumeric(value: any) {
    if (value) {
      let valueArray = [];
      Array.from(value).forEach((elem: any) => {
        if ((/^\d*[a-zA-Z]*$/.test(elem))) {
          valueArray.push(elem);
        }
      });
      if (valueArray.length > 0) {
        return valueArray.join('');
      }
      else return null;
    }
    else return value;
  }
  getRehabPatientTransactions(request: any): Observable<PatientTransactionRehabAPIResponse> {
    const body = {
      facilityId: request.facilityId,
      isMonthly: request.isMonthly,
      startDate: request.startDate,
      endDate: request.endDate,
      currentPageNumber: request.pageNumber,
      numberOfItemsOnPage: request.pageSize,
      ascending: false,
      search: "",
      orderBy: 'encounterid',
      modifiedBy: this.commonService.getEmail()
    };
    const httpParams = new HttpParams().set('hospitalTeamName', request.teamName);
    return this.http.post<PatientTransactionRehabAPIResponse>(environment.urls.getRehabPatientTransactions, body, { params: httpParams })
      .pipe(
        map((response: any) => {
          if (response.data && response.data.length) {
            for (let i = 0; i < response.data.length; i++) {
              response.data[i].los = this.calculateLOS(response.data[i].admitDate, response.data[i].dischargedDate);
            }
          }
          return response;
        }),
        catchError(error => throwError(error))
      );
  }

  getRehabPatientTransactionById(residentTransactionId, hospitalId, teamName: string): Observable<PatientTransactionRehabAPIResponse> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    const body = {
      facilityId: hospitalId,
      rehabTransactionId: residentTransactionId
    };

    return this.http.post<PatientTransactionRehabAPIResponse>(environment.urls.getRehabPatientTransactionById, body, { params: httpParams });
  }

  saveRehabPatientTransaction(body, teamName: string): Observable<any> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    if (body.rehabTransactionId) {
      return this.http.put(environment.urls.updateRehabpatientTransaction, body, { params: httpParams });
    } else {
      return this.http.post(environment.urls.saveRehabpatientTransaction, body, { params: httpParams });
    }
  }

  deleteRehabPatientTransaction(transactionId, facilityId, teamName: string): Observable<any> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    const body = {
      facilityId: facilityId,
      rehabTransactionId: transactionId
    };
    return this.http.post(environment.urls.deleteRehabpatientTransaction, body, { params: httpParams });
  }
  getRehabDuplicateRecord(mrnValue, facilityId, teamName, admitDate, pinValue): Observable<any> {
    const httpParams = new HttpParams().set('mrn', mrnValue).set('facilityId', facilityId).set('hospitalTeamName', teamName).set('admitDate', admitDate).set('pin', pinValue);
    return this.http.get(environment.urls.duplicateRehabPatientTransaction, { params: httpParams });
  }

  // LTACH Patient Transaction Methods
  getLTACHPatientTransactions(request: any): Observable<PatientTransactionLTACHAPIResponse> {
    const body = {
      facilityId: request.facilityId,
      isMonthly: request.isMonthly,
      startDate: request.startDate,
      endDate: request.endDate,
      currentPageNumber: request.pageNumber,
      numberOfItemsOnPage: request.pageSize,
      ascending: false,
      search: "",
      orderBy: 'encounterid',
      modifiedBy: this.commonService.getEmail()
    };
    const httpParams = new HttpParams().set('hospitalTeamName', request.teamName);
    return this.http.post<PatientTransactionLTACHAPIResponse>(environment.urls.getLTACHPatientTransactions, body, { params: httpParams })
      .pipe(
        map((response: any) => {
          if (response.data && response.data.length) {
            for (let i = 0; i < response.data.length; i++) {
              response.data[i].los = this.calculateLOS(response.data[i].admitDate, response.data[i].dischargedDate);

              if (response.data[i].nutritionIntervention) {
                response.data[i].nutritionIntervention = response.data[i].nutritionIntervention.join(', ');
              }
              if (response.data[i].patientUnit) {
                response.data[i].patientUnit = response.data[i].patientUnit.join(', ');
              }
            }
          }
          return response;
        }),
        catchError(error => throwError(error))
      );
  }

  getLTACHPatientTransactionById(ltachTransactionId, hospitalId, teamName: string): Observable<PatientTransactionLTACHAPIResponse> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    const body = {
      facilityId: hospitalId,
      id: ltachTransactionId
    };

    return this.http.post<PatientTransactionLTACHAPIResponse>(environment.urls.getLTACHPatientTransactionById, body, { params: httpParams });
  }

  saveLTACHPatientTransaction(body, teamName: string): Observable<any> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    if (body.ltachTransactionId) {
      return this.http.put(environment.urls.updateLTACHPatientTransaction, body, { params: httpParams });
    } else {
      return this.http.post(environment.urls.saveLTACHPatientTransaction, body, { params: httpParams });
    }
  }

  deleteLTACHPatientTransaction(transactionId, facilityId, teamName: string): Observable<any> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    const body = {
      facilityId: facilityId,
      id: transactionId
    };
    return this.http.post(environment.urls.deleteLTACHPatientTransaction, body, { params: httpParams });
  }

  getLTACHDuplicateRecord(mrnValue, facilityId, teamName, admitDate, pinValue): Observable<any> {
    const httpParams = new HttpParams().set('mrn', mrnValue).set('facilityId', facilityId).set('hospitalTeamName', teamName).set('admitDate', admitDate).set('pin', pinValue);
    return this.http.get(environment.urls.duplicateLTACHPatientTransaction, { params: httpParams });
  }

  // PAC (Pediatric Acute Care) Patient Transaction Methods
  getPACPatientTransactions(request: any): Observable<any> {
    const body = {
      facilityId: request.facilityId,
      isMonthly: request.isMonthly,
      startDate: request.startDate,
      endDate: request.endDate,
      currentPageNumber: request.pageNumber,
      numberOfItemsOnPage: request.pageSize,
      ascending: false,
      search: "",
      orderBy: 'encounterid',
      modifiedBy: this.commonService.getEmail()
    };
    const httpParams = new HttpParams().set('hospitalTeamName', request.teamName);
    return this.http.post<any>(environment.urls.getPACPatientTransactions, body, { params: httpParams })
      .pipe(
        map((response: any) => {
          if (response.data && response.data.length) {
            for (let i = 0; i < response.data.length; i++) {
              response.data[i].los = this.calculateLOS(response.data[i].admitDate, response.data[i].dischargedDate);

              if (response.data[i].rdInterventions) {
                response.data[i].rdInterventions = response.data[i].rdInterventions.join(', ');
              }
              if (response.data[i].patientUnit) {
                response.data[i].patientUnit = response.data[i].patientUnit.join(', ');
              }
            }
          }
          return response;
        }),
        catchError(error => throwError(error))
      );
  }

  getPACPatientTransactionById(pacTransactionId, hospitalId, teamName: string): Observable<any> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    const body = {
      facilityId: hospitalId,
      id: pacTransactionId
    };

    return this.http.post<any>(environment.urls.getPACPatientTransactionById, body, { params: httpParams });
  }

  savePACPatientTransaction(body, teamName: string): Observable<any> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    if (body.patientTransactionId) {
      return this.http.put(environment.urls.updatePACPatientTransaction, body, { params: httpParams });
    } else {
      return this.http.post(environment.urls.savePACPatientTransaction, body, { params: httpParams });
    }
  }

  deletePACPatientTransaction(transactionId, facilityId, teamName: string): Observable<any> {
    const httpParams = new HttpParams().set('hospitalTeamName', teamName);
    const body = {
      facilityId: facilityId,
      id: transactionId
    };
    return this.http.post(environment.urls.deletePACPatientTransaction, body, { params: httpParams });
  }

  getPACDuplicateRecord(mrnValue, facilityId, admitDate, pinValue): Observable<any> {
    let httpParams = new HttpParams().set('facilityId', facilityId);
    if (mrnValue) { httpParams = httpParams.set('mrn', mrnValue); }
    if (admitDate) { httpParams = httpParams.set('admitDate', admitDate); }
    if (pinValue) { httpParams = httpParams.set('pin', pinValue); }
    return this.http.get(environment.urls.duplicatePACPatientTransaction, { params: httpParams });
  }

}
